import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Statistic,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import lightappService from '../../services/lightappService';

const { Option } = Select;

const Endpoints = () => {
  const [loading, setLoading] = useState(false);
  const [endpoints, setEndpoints] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [form] = Form.useForm();

  useEffect(() => {
    fetchEndpoints();
  }, []);

  const fetchEndpoints = async () => {
    setLoading(true);
    try {
      const response = await lightappService.getEndpoints();
      if (response.success) {
        setEndpoints(response.data?.list || response.data || []);
        calculateStats(response.data?.list || response.data || []);
      } else {
        message.error(response.error || 'Failed to fetch endpoints');
      }
    } catch (error) {
      message.error('Failed to fetch endpoints');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const total = data.length;
    const active = data.filter((e) => e.status === 'active').length;
    const inactive = total - active;
    setStats({ total, active, inactive });
  };

  const handleAdd = () => {
    setEditingEndpoint(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingEndpoint(record);

    // Parse portainer_url into host and port
    let host = '';
    let port = 2375;
    let tls_config = 'none';

    if (record.portainer_url) {
      try {
        const url = new URL(record.portainer_url);
        host = url.hostname;
        port = url.port || (url.protocol === 'https:' ? 443 : 80);
        tls_config = url.protocol === 'https:' ? 'tls' : 'none';
      } catch (e) {
        // Fallback: try to parse manually
        const match = record.portainer_url.match(/^(https?):\/\/([^:]+):?(\d+)?/);
        if (match) {
          host = match[2];
          port = match[3] || (match[1] === 'https' ? 443 : 80);
          tls_config = match[1] === 'https' ? 'tls' : 'none';
        }
      }
    }

    form.setFieldsValue({
      name: record.name,
      host,
      port,
      agent_type: record.type === 'agent' ? 'agent' : 'tcp',
      tls_config: record.tls_config || tls_config,
      wss_url: record.wss_url,
      // 凭据不再回显(后端 Endpoint::$hidden 隐藏 api_token/api_key)。编辑时留空=保持不变。
      api_token: undefined,
      node_id: record.node_id,
      description: record.description,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await lightappService.deleteEndpoint(id);
      if (response.success) {
        message.success('Endpoint deleted successfully');
        fetchEndpoints();
      } else {
        message.error(response.error || 'Failed to delete endpoint');
      }
    } catch (error) {
      message.error('Failed to delete endpoint');
    }
  };

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const response = await lightappService.testEndpoint(id);
      if (response.success) {
        message.success('Connection test successful!');
        fetchEndpoints();
      } else {
        message.error(response.error || 'Connection test failed');
      }
    } catch (error) {
      message.error('Connection test failed');
    } finally {
      setTestingId(null);
    }
  };

  const handleSubmit = async (values) => {
    try {
      // Build portainer_url from host and port
      const protocol = values.tls_config && values.tls_config !== 'none' ? 'https' : 'http';
      const portainer_url = `${protocol}://${values.host}:${values.port}`;

      const data = {
        name: values.name,
        portainer_url,
        wss_url: values.wss_url,
        api_key: values.api_token, // Use api_token as api_key for backwards compatibility
        api_token: values.api_token,
        node_id: values.node_id ? parseInt(values.node_id, 10) : null,
        type: values.agent_type === 'agent' ? 'agent' : 'portainer',
        node_type: 'docker',
        tls_config: values.tls_config,
        description: values.description,
      };

      let response;
      if (editingEndpoint) {
        response = await lightappService.updateEndpoint(editingEndpoint.id, data);
      } else {
        response = await lightappService.createEndpoint(data);
      }

      if (response.success) {
        message.success(
          `Endpoint ${editingEndpoint ? 'updated' : 'created'} successfully`
        );
        setModalVisible(false);
        fetchEndpoints();
      } else {
        message.error(response.error || 'Failed to save endpoint');
      }
    } catch (error) {
      message.error('Failed to save endpoint');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <ApiOutlined />
          <span style={{ fontWeight: 500 }}>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Host',
      key: 'host',
      render: (_, record) => {
        if (!record.portainer_url) return '-';
        try {
          const url = new URL(record.portainer_url);
          const port = url.port || (url.protocol === 'https:' ? 443 : 80);
          return `${url.hostname}:${port}`;
        } catch (e) {
          return record.portainer_url;
        }
      },
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const t = type || 'portainer';
        return <Tag color={t === 'agent' ? 'green' : 'blue'}>{t.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const isActive = status === 'active' || status === 1;
        return (
          <Tag
            icon={isActive ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
            color={isActive ? 'success' : 'default'}
          >
            {isActive ? 'Active' : 'Inactive'}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<SyncOutlined spin={testingId === record.id} />}
            onClick={() => handleTest(record.id)}
            loading={testingId === record.id}
          >
            Test
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this endpoint?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic title="节点总数" value={stats.total} />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="活跃"
                value={stats.active}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="未激活"
                value={stats.inactive}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      <Card
        title="Docker 节点管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Endpoint
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={endpoints}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No endpoints yet. Click “Add Endpoint” to connect a Docker host."
              />
            ),
          }}
        />
      </Card>

      <Modal
        title={editingEndpoint ? 'Edit Endpoint' : 'Add Endpoint'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Endpoint Name"
            rules={[{ required: true, message: 'Please enter endpoint name' }]}
          >
            <Input placeholder="e.g., Production Server 1" />
          </Form.Item>

          <Form.Item
            name="host"
            label="Host"
            rules={[{ required: true, message: 'Please enter host' }]}
          >
            <Input placeholder="e.g., 192.168.1.100 or docker.example.com" />
          </Form.Item>

          <Form.Item
            name="port"
            label="Port"
            rules={[{ required: true, message: 'Please enter port' }]}
            initialValue={2375}
          >
            <Input type="number" placeholder="2375 (HTTP) or 2376 (HTTPS)" />
          </Form.Item>

          <Form.Item
            name="agent_type"
            label="Connection Type"
            initialValue="tcp"
          >
            <Select>
              <Option value="tcp">TCP (Docker API)</Option>
              <Option value="agent">Agent (Custom)</Option>
              <Option value="ssh">SSH</Option>
            </Select>
          </Form.Item>

          <Form.Item name="tls_config" label="TLS Configuration" initialValue="none">
            <Select>
              <Option value="none">No TLS</Option>
              <Option value="tls">TLS</Option>
              <Option value="tls_verify">TLS with Verification</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="wss_url"
            label="WebSocket URL (Terminal)"
            tooltip="WebSocket URL for terminal access, e.g., wss://terminal.example.com"
          >
            <Input placeholder="e.g., wss://terminal.example.com or ws://192.168.1.100:8089" />
          </Form.Item>

          <Form.Item
            name="api_token"
            label="API Token"
            tooltip="Authentication token for agent API. 出于安全,已保存的凭据不再回显;编辑时留空表示保持不变。"
          >
            <Input.Password placeholder={editingEndpoint ? '留空则保持原凭据不变' : 'API token for agent authentication'} />
          </Form.Item>

          <Form.Item
            name="node_id"
            label="Node ID"
            tooltip="Node ID for agent authentication (used in token hash)"
          >
            <Input type="number" placeholder="e.g., 4" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Endpoints;
